import React, { useCallback, useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { useQuery } from '@apollo/react-hooks';
import {
  AnimatedScreen,
  PageHeader,
  DEFAULT_RESULTS_PER_PAGE,
  Paginate,
  PaginationType,
  EmptyTable,
  Table,
  FormInput,
  UserStatusPill,
} from 'Shared/components';
import { GET_USERS_QUERY } from 'Admin/Users/graphql';
import { PortalUserGrant } from 'Admin/Users/types';
import { getRoleFromGrants } from 'Admin/utils';
import { ClearFilterButton } from 'Shared/components/ClearFilterButtons';
import { objectToParams, paramsFromSearchString } from 'Shared/utils';
import { Formik } from 'formik';

import styles from './styles.module.css';

type Data = {
  users: {
    pagination: PaginationType;
    records: Array<{
      id: string;
      activated: boolean;
      firstName: string;
      lastName: string;
      email: string;
      licensedAgent: {
        nationalProducerNumber: string;
      } | null;
      partner: {
        id: string;
        name: string;
      } | null;
      portalUserGrants: Array<PortalUserGrant>;
    }>;
  };
};

type FilterType = {
  firstName?: string;
  lastName?: string;
  email?: string;
  nationalProducerNumber?: string;
};

const ALLOWED_FILTERS: Array<keyof FilterType> = [
  'firstName',
  'lastName',
  'email',
  'nationalProducerNumber',
];

export const UsersHome: React.FC<RouteComponentProps> = ({ history }) => {
  const [resultsPerPage, changeResultsPerPage] = useState(
    DEFAULT_RESULTS_PER_PAGE
  );
  const [filters, setFilters] = useState<FilterType>({});
  const { networkStatus, error, data, fetchMore } = useQuery<Data>(
    GET_USERS_QUERY,
    {
      fetchPolicy: 'cache-and-network',
      variables: {
        paginate: {
          limit: resultsPerPage,
          offset: 0,
        },
        filters: filters,
      },
      notifyOnNetworkStatusChange: true,
    }
  );

  useEffect(() => {
    const searchParams =
      history.location.search && history.location.search !== '?'
        ? paramsFromSearchString(history.location.search)
        : {};

    const allowedUrlFilters = ALLOWED_FILTERS.reduce((acc: FilterType, key) => {
      acc[key] = searchParams[key];
      return acc;
    }, {});

    const compactedFilters = compactFilters(allowedUrlFilters);

    setFilters(compactedFilters);
  }, [history.location.search]);

  const compactFilters = (newFilters: FilterType): FilterType => {
    return Object.entries(newFilters).reduce(
      (compacted: FilterType, [filterKey, filterValue]) => {
        if (filterValue) {
          return {
            ...compacted,
            [filterKey]: filterValue.trim(),
          };
        }

        return compacted;
      },
      {}
    );
  };

  const updateFilters = useCallback(
    (newFilters: FilterType) => {
      const compactedFilters = compactFilters(newFilters);
      history.replace({
        pathname: history.location.pathname,
        search: `?${objectToParams(compactedFilters)}`,
      });
      setFilters(compactedFilters);
    },
    [history]
  );

  const clearAllFilters = useCallback(
    () =>
      setFilters({
        firstName: '',
        lastName: '',
        email: '',
        nationalProducerNumber: '',
      }),
    [setFilters]
  );

  const users = data?.users?.records;
  const pagination = data?.users?.pagination;

  return (
    <AnimatedScreen
      testId="all-users-home"
      loading={networkStatus === 1}
      error={error && error.message}
    >
      <PageHeader heading="Users" />
      <Formik
        enableReinitialize
        initialValues={filters}
        onSubmit={updateFilters}
      >
        {({ handleSubmit, values, handleChange }) => {
          return (
            <form
              onSubmit={handleSubmit}
              className={styles.search}
              data-testid="form"
            >
              <div className={styles.inputContainer}>
                <FormInput
                  name="firstName"
                  className={styles.searchInput}
                  value={values.firstName}
                  label="Search first name"
                  variant="search"
                  onChange={handleChange}
                  onIconClick={() => {
                    handleSubmit();
                  }}
                />
                <FormInput
                  name="lastName"
                  className={styles.searchInput}
                  value={values.lastName}
                  label="Search last name"
                  variant="search"
                  onChange={handleChange}
                  onIconClick={() => {
                    handleSubmit();
                  }}
                />
                <FormInput
                  name="email"
                  className={styles.searchInput}
                  value={values.email}
                  label="Search email"
                  variant="search"
                  onChange={handleChange}
                  onIconClick={() => {
                    handleSubmit();
                  }}
                />
                <FormInput
                  name="nationalProducerNumber"
                  className={styles.searchInput}
                  value={values.nationalProducerNumber}
                  label="Search NPN"
                  variant="search"
                  onChange={handleChange}
                  onIconClick={() => {
                    handleSubmit();
                  }}
                />
              </div>
              <button type="submit" className={styles.submit} />
              <div className={styles.clearContainer}>
                <ClearFilterButton
                  disabled={!Object.keys(filters).length}
                  onClick={clearAllFilters}
                  data-testid="clear-filters"
                >
                  Clear Search
                </ClearFilterButton>
              </div>
            </form>
          );
        }}
      </Formik>

      {users && users.length ? (
        <>
          <Table
            columnWidths={['118px', 2, 2, 3, 1, 2, 2]}
            headers={[
              'Status',
              'First Name',
              'Last Name',
              'Email',
              'NPN',
              'Agency',
              'Role(s)',
            ]}
            rows={users.map((user) => ({
              key: user.id,
              onViewRow: () => {
                user.partner
                  ? history.push(
                      `/admin/partners/${user.partner.id}/users/${user.id}`
                    )
                  : history.push(`/admin/users/${user.id}`);
              },
              data: [
                <UserStatusPill activated={user.activated} />,
                user.firstName,
                user.lastName,
                user.email,
                user.licensedAgent?.nationalProducerNumber,
                user.partner && user.partner.name,
                getRoleFromGrants(user.portalUserGrants, user.partner?.id),
              ],
            }))}
            loading={[2, 3, 4].includes(networkStatus)}
            hasPagination
          />
          <Paginate
            total={pagination?.total || 0}
            offset={pagination?.offset}
            limit={pagination?.limit}
            onOffsetChange={(limit, offset) => {
              fetchMore({
                variables: { paginate: { offset, limit } },
                updateQuery: (prev, { fetchMoreResult }) => {
                  if (!fetchMoreResult) return prev;
                  return {
                    ...prev,
                    ...fetchMoreResult,
                  };
                },
              });
            }}
            onLimitChange={changeResultsPerPage}
          />
        </>
      ) : (
        <EmptyTable>There are no system users.</EmptyTable>
      )}
    </AnimatedScreen>
  );
};
